// @flow
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-http-source-selector';
import './InternalBunnyVideo.css';
import { productColor } from './designSystem.js';

type ReturnType<F> = $Call<<T>((...Iterable<any>) => T) => T, F>;

// TODO videojs doesn't expose flow types so this is just going to
// be the any type for now. Consider writing our own flow types for
// this package at some point. Or start converting this whole
// project to typescript...
type Player = ReturnType<typeof videojs>;

const cdnOrigin = 'https://videos.mathspace.co';

function noop() {} // For referential equality

export function InternalBunnyPopover({
  videoId,
  onClose,
}: {
  videoId: string, // eg. 5e7aee5a-5c7d-41d7-af17-ac58bcd4f3a6
  onClose: () => void,
}) {
  // Our aspect ratios are all over the shop (278 distinct aspect ratios 😱)
  // The most frequent aspect ratio (at time of writing) is 4:3.
  // See here for some analysis:
  // https://paper.dropbox.com/doc/Wistia-Video-Aspect-Ratio-Determination--BhWNRojL805v05axiESTRSoMAg-T0kXoXIM3zvIuBEPoHrLv
  const aspectRatio = 4 / 3;
  const width = 716 + 2 * 16; // 748
  const height = width / aspectRatio; // 561
  const popoverBorderRadius = 8;

  const [isUserActive, setIsUserActive] = useState(false);
  const onUserActiveChange = useCallback((isActive: boolean) => {
    setIsUserActive(isActive);
  }, []);
  const [playbackWasInitiated, setPlaybackWasInitiated] = useState(false);
  const onFirstPlaybackInitiated = useCallback(() => {
    setPlaybackWasInitiated(true);
  }, []);

  // There are two scenarios when we want to show our close overlay:
  //   1) When playback has never been initiated
  //   2) When the user has recently moved their cursor over the video (ie. isUserActive)
  const showCloseOverlay = playbackWasInitiated === false || isUserActive;

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          position: 'absolute',
          top: 16,
          left: -16,
          boxShadow: '0px 8.75px 49px rgba(62, 62, 76, 0.4)',
          borderRadius: popoverBorderRadius,
          overflow: 'hidden',
          zIndex: 1,
        }}
      >
        <InternalBunnyVideo
          videoId={videoId}
          width={width}
          height={height}
          borderRadius={popoverBorderRadius}
          onFirstPlaybackInitiated={onFirstPlaybackInitiated}
          onUserActiveChange={onUserActiveChange}
        />
        <CloseOverlay isVisible={showCloseOverlay} onClose={onClose} />
      </div>
    </div>
  );
}

function CloseOverlay({
  isVisible,
  onClose,
}: {
  isVisible: boolean,
  onClose: () => void,
}) {
  const [btnHovered, setBtnHovered] = useState(false);
  const transitionDuration = '200ms';

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 100,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.5), transparent)',
          opacity: isVisible ? '1' : 0,
          transition: `opacity ${transitionDuration} ease`,
          // We want the pointer events to pass through to the underlaid
          // WistiaVideo as it has internal hover-tracking for displaying
          // it's controls that we don't want to interfere with.
          pointerEvents: 'none',
        }}
      />
      <button
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        onClick={onClose}
        style={{
          background: `rgba(0, 0, 0, ${btnHovered ? 0.2 : 0})`,
          borderRadius: 4,
          border: 'none',
          padding: 2,
          position: 'absolute',
          top: 5,
          right: 5,
          opacity: isVisible ? 1 : 0,
          transition: `background opacity ${transitionDuration} ease`,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
      >
        <CloseIcon />
      </button>
    </>
  );
}

function CloseIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="#fff"
      style={{ display: 'block' }}
    >
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </svg>
  );
}

export function InternalBunnyVideo({
  videoId,
  width,
  height,
  borderRadius = 0,
  onFirstPlaybackInitiated = noop,
  onUserActiveChange = noop,
}: {
  videoId: string,
  width: number,
  height: number,
  // You should always provide a border radius if you want an
  // upstream consumer to clip. This is because internally we
  // handle fixing a compositing bug in Blink based on this.
  borderRadius?: number | void,
  // Always ensure this prop is memoized as its a dep to an effect
  onFirstPlaybackInitiated?: () => void,
  // This is a videojs concept, a user appears to be considered "active"
  // if they have recently moved their cursor over the video, and inactive
  // after a given (configurable) inactivityTimeout period.
  onUserActiveChange?: (isActive: boolean) => void,
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // There is a compositing? bug in the Blink rendering engine (Chrome/Edge)
  // which produces aliasing artifacts on the rounded corners when compositing
  // a clipped media layer (the video) with a separate, non-transparent
  // background layer. If we have a video that perfectly fills the available
  // space, this bug leads to a pixelated black border on the rounded corners,
  // which looks awful. To avoid this bug, we only show the black background
  // if the video layout leads to a black fill around the video at-least equal
  // in size to the radius of the border. This ensures there is no
  // intersection between the media layer, and the clipping path.
  const [showBackground, setShowBackground] = useState(true);

  // Initialize a single Player instance
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const videoJsElement = document.createElement('video-js');
    container.appendChild(videoJsElement);
    // For all options see: https://videojs.com/guides/options/
    const player = (playerRef.current = videojs(videoJsElement, {
      controls: true,
      // Required for captions to fetch with CORS
      crossorigin: 'anonymous',
      playbackRates: [0.5, 1, 1.5, 2],
      preload: 'auto',
      // Adapt the player UI based on layout dimensions
      responsive: true,
      // Ensure it expands to fill the dimensions of the container
      fill: true,
      html5: {
        // vhs stands for videojs http streaming.
        // It's the part responsible for adaptively choosing an appropriate
        // quality stream based on bandwidth, video dimensions, etc.
        vhs: {
          // The dimensions of the HTMLVideoElement DOM node affect which
          // quality level is chosen. But by default they stupidly use the
          // CSS pixels, not the actual pixels to determine this, which means
          // they choose the wrong stream 100% of the time on High DPI displays.
          // This means the video is always blurry. Here we configure it to use
          // the CSS pixel dimensions after being scaled by the devicePixelRatio,
          // which gives a rough approximation of the actual pixels used to
          // render the video.
          useDevicePixelRatio: true,
        },
      },
      plugins: {
        // This interacts with the 'videojs-http-source-selector' plugin
        // in order to display the source (aka resolution/quality) selector
        httpSourceSelector: {
          default: 'auto',
        },
      },
    }));

    // Initialises the videojs-http-source-selector plugin
    player.httpSourceSelector();

    // We must manually cleanup the player otherwise we leak memory
    return () => {
      if (player.isDisposed()) return;
      player.dispose();
      // Explicitly null out the ref so that other effect cleanups
      // which will be scheduled after this one will bail out early.
      playerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    if (player === null) return;

    function _onUserActiveChange(event: Event) {
      const isActive = event.type === 'useractive';
      onUserActiveChange(isActive);
    }

    player.on('useractive', _onUserActiveChange);
    player.on('userinactive', _onUserActiveChange);

    return () => {
      player.off('useractive', _onUserActiveChange);
      player.off('userinactive', _onUserActiveChange);
    };
  }, []);

  // Prevent halo artifacts around the video once its loaded. Two situations
  // that cause this that need to be handled:
  // - Crappily made videos that don't match a standard aspect ratio
  // - Compositing bug in blink when clipping a border raidus
  useEffect(() => {
    const player = playerRef.current;
    if (player === null) return;

    // Video.js has a shit API that prevents us from looking at the
    // ready state. We can route around this limitation if we assume
    // we're always using the HTML5 "backend" (aka "tech")
    // TODO look into how the "tech" (aka backend) is chosen internally.
    const videoEl = player.tech_.el_;
    if (!(videoEl instanceof HTMLVideoElement)) {
      throw Error('Unexpected tech being used for video playback');
    }

    function preventHaloArtifactsAroundVideo() {
      // Typescript is an idiot and thinks a const binding can be changed...
      if (player === null) return;

      const layout = computeVideoLayout({
        targetDimensions: { width, height },
        dimensions: {
          width: player.videoWidth(),
          height: player.videoHeight(),
        },
      });

      const isBlink: boolean =
        // $FlowIgnore userAgentData is only in some browsers atm
        navigator.userAgentData?.brands.some(
          (brand: { brand: string }) => brand.brand === 'Chromium',
        ) ?? false;

      // This is to account for our many horrendously recorded videos which
      // are recorded at whacky non-standard aspect ratios. This often leads
      // to hairline black artifacts on the outside of the video as it may
      // be 0.3px narrower than the container size, so the background leaks
      // through. We'll still get stupid hard black borders beyond this, but
      // this will remove any sub-pixel grossness.
      const minThreshold = 1.99;
      // If we have a border radius > minThreshold, and the Blink engine is
      // in use, we use the border radius as the threshold as anything less
      // than that will elicit the compositing bug.
      //
      // TODO with massive borderRadius, and really poorly recorded video
      // this will leave big whitespace gaps on either vertical or lateral sides.
      const threshold = isBlink
        ? Math.max(minThreshold, borderRadius)
        : minThreshold;
      const showBlackBackground =
        layout.horizontalGap > threshold || layout.verticalGap > threshold;
      setShowBackground(showBlackBackground);
    }

    // If the metadata is already available, we must do things synchronously
    if (videoEl.readyState >= videoEl.HAVE_METADATA) {
      preventHaloArtifactsAroundVideo();
      return;
    } else {
      // Otherwise, setup a listener to await metadata being available.
      player.one('loadedmetadata', preventHaloArtifactsAroundVideo);
      return () => {
        if (player.isDisposed()) return;
        player.off('loadedmetadata', preventHaloArtifactsAroundVideo);
      };
    }
  }, [width, height, borderRadius]);

  // Initialize the Player instance with our video, and any associated captions
  useEffect(() => {
    const player = playerRef.current;
    if (player === null) return;

    const loadStartTime = Date.now();
    let timeoutId: TimeoutID;

    function onLoadedMetadata() {
      // Once metadata is loaded, the first frame of the video should
      // be rendered on screen, so we can remove the loading UI.

      // We enforce a minimum loading state duration of 500ms to prevent quick
      // flashes of the loading state in cases where the load was super quick
      // (typically this occurs when the assets are cached locally)
      const minLoadingStateDuration = 500;
      const loadingDuration = Date.now() - loadStartTime;
      if (loadingDuration >= minLoadingStateDuration) {
        setIsLoading(false);
      } else {
        const timeoutDuration = minLoadingStateDuration - loadingDuration;
        timeoutId = setTimeout(() => {
          setIsLoading(false);
        }, timeoutDuration);
      }
    }

    function onPlay() {
      onFirstPlaybackInitiated();
    }

    setIsLoading(true);
    player.one('loadedmetadata', onLoadedMetadata);
    player.one('play', onPlay);

    player.src({
      src: `${cdnOrigin}/${videoId}/playlist.m3u8`,
      type: 'application/x-mpegURL',
    });

    const abortController = new AbortController();
    getVideoCaptions({
      videoId,
      signal: abortController.signal,
    })
      .then((captions) => {
        for (const caption of captions) {
          player.addRemoteTextTrack({
            kind: 'captions',
            src: caption.url,
            label: caption.language.label,
            srclang: caption.language.lang,
            default: caption.language.default,
          });
        }
      })
      .catch((_error) => {
        // TODO decide how to do logging in jsx content...
      });

    return () => {
      abortController.abort();
      player.off('loadedmetadata', onLoadedMetadata);
      player.off('play', onPlay);
      clearTimeout(timeoutId);
    };

    // TODO intentionally not including onFirstPlaybackInitiated prop as a dep
    // as we don't want to reinitialize if that changes. Figure out some way
    // in which we can update the one('play') subscription if the prop changes
    // and we haven't yet previously fired it (as the prop may change after
    // the first playback has already occurred)
  }, [videoId]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={containerRef}
        style={{
          width,
          height,
          background: showBackground ? 'black' : 'transparent',
          borderRadius,
          overflow: 'clip',
          // Consumed in BunnyVideo.css to fix a compositing issue in Blink
          '--control-bar-border-radius': `${borderRadius}px`,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          // width/height must match the video being covered
          width,
          height,
          background: productColor.cloudburst,
          borderRadius,
          overflow: 'clip',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isLoading ? 1 : 0,
          pointerEvents: isLoading ? 'auto' : 'none',
          transition: 'opacity 300ms ease',
          color: productColor.white,
          fontWeight: 600,
        }}
      >
        Loading video...
      </div>
    </div>
  );
}

async function getVideoCaptions({
  videoId,
  signal,
}: {
  videoId: string,
  signal: AbortSignal,
}) {
  const languages = [
    { lang: 'en', label: 'English', default: true },
    { lang: 'es', label: 'Español', default: false },
  ];

  // NOTE we're only fetching to check the existence of the .vtt file
  const fetchCaptions = (language: $ElementType<typeof languages, number>) => {
    const url = `${cdnOrigin}/${videoId}/captions/${language.lang}.vtt`;
    return fetch(url, { method: 'HEAD', signal })
      .then((response) => {
        if (response.ok) return { url, language };
        // We expect a 404 when the video has no captions available
        if (response.status === 404) return null;
        throw Error(`Failed to fetch ${url}: ${response.status}`);
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // AbortError is expected when we call .abort()
          return null;
        }
        throw Error(`Failed to fetch ${url}: ${error}`);
      });
  };

  const results = await Promise.allSettled(
    languages.map((lang) => fetchCaptions(lang)),
  );

  return results
    .map((result) => (result.status === 'fulfilled' ? result.value : null))
    .filter(Boolean);
}

// Describes the layout of the video within its container.
// The video is proportionally scaled to fit within the container
// and the transform origin is the center of the container.
type VideoLayout = {
  width: number,
  height: number,
  // The size of the gap between the video and the top/bottom
  // edge of the container
  verticalGap: number,
  // The size of the gap between the video and the left/right
  // edge of the container
  horizontalGap: number,
};

function computeVideoLayout({
  targetDimensions: td,
  dimensions: d,
}: {
  targetDimensions: { width: number, height: number },
  dimensions: { width: number, height: number },
}): VideoLayout {
  const targetAspectRatio = td.width / td.height;
  const aspectRatio = d.width / d.height;

  if (aspectRatio >= targetAspectRatio) {
    // too much width relative to height; width will fill available space
    const scale = td.width / d.width;
    const rescaledHeight = d.height * scale;
    return {
      width: td.width,
      height: rescaledHeight,
      verticalGap: (td.height - rescaledHeight) / 2,
      horizontalGap: 0,
    };
  } else {
    // too much height relative to width; height will fill available space
    const scale = td.height / d.height;
    const rescaledWidth = d.width * scale;
    return {
      width: rescaledWidth,
      height: td.height,
      verticalGap: 0,
      horizontalGap: (td.width - rescaledWidth) / 2,
    };
  }
}
